import { useEffect } from 'react';
import { X, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

export default function ServiceModal({ isOpen, onClose, service, colorScheme = 'blue' }) {
  // Color mappings
  const colors = {
    green: {
      shadow: 'rgba(34, 197, 94, 0.4)',
      border: 'from-green-400 via-green-500 to-green-400',
      header: 'from-green-800 via-green-600 via-green-500 via-green-600 to-green-800',
      text: 'text-green-100',
      accent: 'from-green-50 to-green-50',
      borderColor: 'border-green-100 hover:border-green-300',
      dot: 'from-green-800 via-green-600 via-green-500 via-green-600 to-green-800'
    },
    teal: {
      shadow: 'rgba(20, 184, 166, 0.4)',
      border: 'from-teal-400 via-cyan-500 to-teal-400',
      header: 'from-teal-800 via-teal-600 via-cyan-500 via-teal-600 to-teal-800',
      text: 'text-teal-100',
      accent: 'from-teal-50 to-cyan-50',
      borderColor: 'border-teal-100 hover:border-teal-300',
      dot: 'from-teal-800 via-teal-600 via-cyan-500 via-teal-600 to-teal-800'
    },
    blue: {
      shadow: 'rgba(59, 130, 246, 0.4)',
      border: 'from-blue-400 via-blue-500 to-blue-400',
      header: 'from-blue-800 via-blue-600 via-blue-500 via-blue-600 to-blue-800',
      text: 'text-blue-100',
      accent: 'from-gray-50 to-blue-50',
      borderColor: 'border-blue-100 hover:border-blue-300',
      dot: 'from-blue-800 via-blue-600 via-blue-500 via-blue-600 to-blue-800'
    },
    orange: {
      shadow: 'rgba(249, 115, 22, 0.4)',
      border: 'from-orange-400 via-orange-500 to-orange-400',
      header: 'from-orange-800 via-orange-600 via-orange-500 via-orange-600 to-orange-800',
      text: 'text-orange-100',
      accent: 'from-orange-50 to-orange-50',
      borderColor: 'border-orange-100 hover:border-orange-300',
      dot: 'from-orange-800 via-orange-600 via-orange-500 via-orange-600 to-orange-800'
    }
  };
  
  const currentColors = colors[colorScheme];
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      window.addEventListener('keydown', handleEscape);
    }
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen || !service) return null;

  const IconComponent = service.icon;

  return (
    <>
      {/* Backdrop */}
      <div 
        className={`fixed inset-0 bg-black/60 backdrop-blur-sm z-50 transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onMouseEnter={onClose}
        data-testid="modal-backdrop"
      />

      {/* Modal */}
      <div 
        className={`fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none`}
      >
        <div
          className={`relative bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[85vh] overflow-hidden pointer-events-auto transform transition-all duration-700 ease-out ${
            isOpen 
              ? 'opacity-100 scale-100 rotate-0' 
              : 'opacity-0 scale-90 -rotate-2'
          }`}
          style={{
            boxShadow: `0 0 60px ${currentColors.shadow}, 0 20px 60px rgba(0, 0, 0, 0.3)`
          }}
          onMouseLeave={onClose}
          data-testid="service-modal"
        >
          {/* Glowing Border Effect */}
          <div className={`absolute inset-0 rounded-2xl bg-gradient-to-r ${currentColors.border} opacity-20 blur-xl`}></div>
          
          {/* Header with Gradient */}
          <div className={`relative bg-gradient-to-r ${currentColors.header} p-8 text-white`}>
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/20 transition-colors"
              data-testid="modal-close-button"
            >
              <X size={24} />
            </button>
            <div className="flex items-center gap-6">
              <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                <IconComponent size={40} />
              </div>
              <div>
                <h2 className="text-4xl font-bold mb-2">{service.title}</h2>
                <p className={`${currentColors.text} text-lg`}>{service.shortDescription}</p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="relative overflow-y-auto max-h-[calc(85vh-200px)] p-8 space-y-6 bg-white">
            <div className="prose max-w-none">
              <p className="text-gray-700 text-lg leading-relaxed">{service.fullContent.intro}</p>
            </div>

            {/* Sections */}
            <div className="space-y-5">
              {service.fullContent.sections.map((section, idx) => (
                <div 
                  key={idx} 
                  className="bg-white p-6 rounded-xl border border-gray-200 hover:border-gray-300 transition-colors"
                >
                  <h3 className="text-xl font-semibold text-gray-900 mb-3 flex items-center gap-3">
                    <div className={`w-3 h-3 bg-gradient-to-r ${currentColors.dot} rounded-full`}></div>
                    {section.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">{section.content}</p>
                </div>
              ))}
            </div>

            {/* Benefits */}
            {service.fullContent.benefits.length > 0 && (
              <div className={`bg-gradient-to-r ${currentColors.accent} p-6 rounded-xl border ${currentColors.borderColor}`}>
                <h3 className="text-2xl font-semibold text-gray-900 mb-5 flex items-center gap-2">
                  <div className={`w-8 h-1 bg-gradient-to-r ${currentColors.dot} rounded-full`}></div>
                  Benefits
                </h3>
                <div className="space-y-4">
                  {service.fullContent.benefits.map((benefit, idx) => (
                    <div key={idx} className="flex items-start gap-3 bg-white p-4 rounded-lg">
                      <CheckCircle className={`${
                        colorScheme === 'green' ? 'text-green-600' :
                        colorScheme === 'teal' ? 'text-teal-600' :
                        colorScheme === 'blue' ? 'text-blue-600' :
                        'text-orange-600'
                      } flex-shrink-0 mt-1`} size={20} />
                      <span className="text-gray-700 leading-relaxed">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Why Choose */}
            {service.fullContent.whyChoose.length > 0 && (
              <div className={`bg-gradient-to-r ${currentColors.accent} p-6 rounded-xl border ${currentColors.borderColor}`}>
                <h3 className="text-2xl font-semibold text-gray-900 mb-5 flex items-center gap-2">
                  <div className={`w-8 h-1 bg-gradient-to-r ${currentColors.dot} rounded-full`}></div>
                  Why Choose Symetrix?
                </h3>
                <div className="space-y-4">
                  {service.fullContent.whyChoose.map((reason, idx) => (
                    <div key={idx} className="flex items-start gap-3 bg-white p-4 rounded-lg">
                      <CheckCircle className={`${
                        colorScheme === 'green' ? 'text-green-600' :
                        colorScheme === 'teal' ? 'text-teal-600' :
                        colorScheme === 'blue' ? 'text-blue-600' :
                        'text-orange-600'
                      } flex-shrink-0 mt-1`} size={20} />
                      <span className="text-gray-700 leading-relaxed">{reason}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="relative bg-gray-50 p-6 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row gap-4 justify-end">
              <Button
                variant="outline"
                onClick={onClose}
                className="px-6"
                data-testid="modal-cancel-button"
              >
                Close
              </Button>
              <Link to="/contact">
                <Button 
                  className="w-full sm:w-auto bg-gradient-to-r from-blue-800 via-blue-600 via-blue-500 via-blue-600 to-blue-800 hover:opacity-90 text-white px-8 shadow-lg"
                  data-testid="modal-contact-button"
                >
                  Get Started with This Service
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
